import { useCallback, useEffect, useRef, useState } from "react";
import useFetch from "hooks/useFetch";
import { Box, IconButton, TablePagination, Typography } from "@mui/material";
import AdminUsersTab from "components/AdminStats/AdminUsersStats/AdminUsersTab";
import SearchBar from "components/SearchBar";
import { IUser } from "interfaces/auth";
import { useTranslation } from "react-i18next";
import useMediaByValue from "hooks/useMediaByValue";
import CustomDatePicker from "./CustomDatePicker";
import { BootstrapTooltip } from "components/PWATabs/mainTab/CommentsTable";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { getToken } from "utils/getToken";
import { debounce } from "utils/debounce";

type DateRangeArray = [Date | string | null, Date | string | null];

const UsersStats = () => {
  const { t } = useTranslation();
  const token = getToken();
  const [users, setUsers] = useState<Array<IUser>>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [filter, setFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRangeArray>([null, null]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 20;
  const isLargeViewPort = useMediaByValue("(min-width:1120px)");
  const abortControllerRef = useRef<AbortController | null>(null);

  const { triggerFetch: getUsers } = useFetch({
    url: "admin/users",
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const buildQueryParams = (page: number, filter: string, dateRange: DateRangeArray) => {
    const params = new URLSearchParams();
    params.append("page", (page + 1).toString());
    params.append("limit", rowsPerPage.toString());

    if (filter) {
      params.append("searchTerm", filter);
    }

    const [firstDate, secondDate] = dateRange;
    if (firstDate) {
      const startDate = new Date(firstDate);
      startDate.setHours(0, 0, 0, 0);
      params.append("dateFrom", startDate.toISOString());

      if (secondDate && firstDate !== secondDate) {
        const endDate = new Date(secondDate);
        endDate.setHours(23, 59, 59, 999);
        params.append("dateTo", endDate.toISOString());
      } else {
        const endDate = new Date(firstDate);
        endDate.setHours(23, 59, 59, 999);
        params.append("dateTo", endDate.toISOString());
      }
    }

    return params.toString();
  };

  const handleGetUsers = useCallback(
    async (currentPage: number, currentFilter: string, currentDateRange: DateRangeArray) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const queryParams = buildQueryParams(currentPage, currentFilter, currentDateRange);
        const response = await getUsers(undefined, `admin/users?${queryParams}`, { signal: controller.signal });

        if (!controller.signal.aborted && response?.success) {
          setUsers(response.users || []);
          setTotalUsers(response.pagination?.totalUsers || 0);
        }
      } catch (error: any) {
        if (error?.name !== "AbortError") {
          console.error("Error fetching users:", error);
        }
      }
    },
    [getUsers]
  );

  const handleGetUsersRef = useRef(handleGetUsers);

  useEffect(() => {
    handleGetUsersRef.current = handleGetUsers;
  }, [handleGetUsers]);

  const debouncedSearchRef = useRef(
    debounce((value: string, currentDateRange: DateRangeArray) => {
      void handleGetUsersRef.current(0, value, currentDateRange);
    }, 500)
  );

  const handleSearch = (value: string) => {
    setFilter(value);
    setPage(0);
    debouncedSearchRef.current(value, dateRange);
  };

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number): void => {
    setPage(newPage);
    void handleGetUsers(newPage, filter, dateRange);
  };

  const handleDateRangeChange = (dates: DateRangeArray) => {
    setDateRange([dates[0], dates[1]]);
    setPage(0);
    void handleGetUsers(0, filter, [dates[0], dates[1]]);
  };

  const handleFilterReset = () => {
    setFilter("");
    setDateRange([null, null]);
    setPage(0);
    void handleGetUsers(0, "", [null, null]);
  };

  useEffect(() => {
    void handleGetUsers(0, "", [null, null]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isLargeViewPort ? "row" : "column",
          justifyContent: "space-between",
          alignItems: isLargeViewPort ? "center" : "flex-start",
          marginBottom: "30px",
        }}
      >
        <Typography
          sx={{
            marginBottom: isLargeViewPort ? 0 : "30px",
            fontFamily: "var(--cygreMedium), sans-serif",
            color: "#2E263D",
            fontSize: "18px",
            lineHeight: "32px",
          }}
        >
          {t("users")}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: isLargeViewPort ? "auto" : "100%",
          }}
        >
          <Box sx={{ display: "flex", gap: "6px" }}>
            <SearchBar
              useSearchValue={true}
              searchValue={filter}
              onSearch={handleSearch}
              placeholder={t("search")}
              customStyles={{
                fontSize: 14,
                boxShadow:
                  "rgba(255, 255, 255, 0.5) 2px 2px 2px 0px inset, rgba(0, 0, 0, 0.1) 7px 7px 20px 0px, rgba(0, 0, 0, 0.1) 4px 4px 5px 0px",
                "& svg": { width: "20px", height: "20px" },
              }}
            />
            <CustomDatePicker pageChange={setPage} onChangeCallback={handleDateRangeChange} />
            <BootstrapTooltip title={t("resetFilter")} placement="top">
              <IconButton
                onClick={handleFilterReset}
                sx={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  backgroundColor: "#F6F7FB",
                  boxShadow:
                    "rgba(255, 255, 255, 0.5) 2px 2px 2px 0px inset, rgba(0, 0, 0, 0.1) 7px 7px 20px 0px, rgba(0, 0, 0, 0.1) 4px 4px 5px 0px",
                }}
              >
                <RestartAltIcon sx={{ color: "#6D6777" }} />
              </IconButton>
            </BootstrapTooltip>
          </Box>
          {!isLargeViewPort && (
            <TablePagination
              rowsPerPageOptions={[]}
              component="div"
              count={totalUsers}
              rowsPerPage={20}
              page={page}
              labelRowsPerPage={null}
              labelDisplayedRows={() => ""}
              onPageChange={handleChangePage}
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-spacer": {
                  display: "none",
                },
                "& .MuiTablePagination-toolbar": {
                  padding: 0,
                  justifyContent: "flex-start",
                },
                "& .MuiTablePagination-actions": {
                  margin: "0px !important",
                },
              }}
            />
          )}
        </Box>
        {isLargeViewPort && (
          <TablePagination
            rowsPerPageOptions={[]}
            component="div"
            count={totalUsers}
            rowsPerPage={20}
            page={page}
            labelRowsPerPage={null}
            labelDisplayedRows={() => ""}
            onPageChange={handleChangePage}
            sx={{
              "& .MuiTablePagination-spacer": {
                display: "none",
              },
              "& .MuiTablePagination-toolbar": {
                padding: 0,
                justifyContent: "flex-start",
              },
            }}
          />
        )}
      </Box>
      <AdminUsersTab totalUsers={totalUsers} usersArr={users} page={page} handleChangePage={handleChangePage} />
    </>
  );
};

export default UsersStats;
